// src/api/vendors.ts
// Vendors REST client — matches shadow onboarding-api/vendors-api contract (§28.518.99.47.d)
//
// Endpoint contract (backend A47-2):
//   GET    /vendors
//   POST   /vendors
//   GET    /vendors/:id
//   PUT    /vendors/:id
//   DELETE /vendors/:id
//   GET    /vendors/:id/employees
//   POST   /vendors/:id/employees
//   POST   /vendors/:id/employees/:eid/activate
//   GET    /vendors/:id/grants
//   POST   /vendors/:id/grants
//   POST   /vendors/:id/grants/:gid/revoke
//
// All routes require Authorization: Bearer <jwt>. Scope-filtered server-side by
// dealer_id / customer_id / property_ids from JWT claims (spec §"Provisioning gates").

const AWS_BASE = process.env.REACT_APP_API_URL || 'https://api.intrlock.io';

function token(): string {
  return localStorage.getItem('intrlock_token') || '';
}

async function req(path: string, init: RequestInit = {}): Promise<any> {
  const res = await fetch(`${AWS_BASE}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token()}`,
      ...(init.headers || {}),
    },
  });
  if (res.status === 204) return null;
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`vendors-api ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

// ─── Types ────────────────────────────────────────────────────────────────

export interface Vendor {
  id: string;
  name: string;
  category?: string | null;
  contact_email?: string | null;
  contact_phone?: string | null;
  legal_name?: string | null;
  website?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  primary_contact_name?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  license_number?: string | null;
  insurance_expires_at?: string | null;
  notes?: string | null;
  logo_url?: string | null;
  is_amenity?: boolean;
  metadata?: Record<string, any>;
  industry_attributes?: Record<string, any>;
  created_by_user_id?: string;
  created_at?: string;
  updated_at?: string;
  revoked_at?: string | null;
  // aggregate fields hydrated by backend
  employee_count?: number;
  grant_count?: number;
  property_ids?: string[];
}

export interface VendorEmployee {
  id: string;
  vendor_id: string;
  user_id?: string | null;
  person_id?: string | null;
  role: 'vendor_admin' | 'vendor';
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  job_title?: string;
  activation_code?: string | null;
  activated_at?: string | null;
  revoked_at?: string | null;
  created_at?: string;
}

export interface VendorGrant {
  id: string;
  vendor_id: string;
  /** Null means the grant applies to every active employee of the vendor. */
  employee_id?: string | null;
  property_id: string;
  granted_by_user_id: string;
  access_type: 'short_term' | 'long_term';
  valid_from: string;
  valid_to?: string | null;
  device_ids: string[];
  allowed_methods: string[]; // 'pin' | 'card' | 'face' | 'relay_button' | 'mobile_app' — BLE excluded
  pin_code?: string | null;
  time_window?: { days?: string[]; start?: string; end?: string } | null;
  notes?: string | null;
  revoked_at?: string | null;
  created_at?: string;
}

// ─── Vendors ──────────────────────────────────────────────────────────────

export function listVendors(params?: { property_id?: string }): Promise<Vendor[]> {
  const qs = params
    ? '?' + new URLSearchParams(params as Record<string, string>).toString()
    : '';
  return req(`/vendors${qs}`).then((r) => (Array.isArray(r) ? r : r?.vendors || r?.items || []));
}

export function getVendor(id: string): Promise<Vendor> {
  return req(`/vendors/${id}`).then((r) => r?.vendor || r);
}

export function createVendor(data: Partial<Vendor>): Promise<Vendor> {
  return req('/vendors', { method: 'POST', body: JSON.stringify(data) }).then((r) => r?.vendor || r);
}

export function updateVendor(id: string, data: Partial<Vendor>): Promise<Vendor> {
  // PUT returns { ok: true }, not the updated vendor. Read the authoritative
  // row back so detail state never gets replaced with the acknowledgement.
  return req(`/vendors/${id}`, { method: 'PUT', body: JSON.stringify(data) }).then(() => getVendor(id));
}

export function deleteVendor(id: string): Promise<null> {
  return req(`/vendors/${id}`, { method: 'DELETE' });
}

// ─── Employees ────────────────────────────────────────────────────────────

export function listVendorEmployees(vendorId: string): Promise<VendorEmployee[]> {
  return req(`/vendors/${vendorId}/employees`).then((r) =>
    Array.isArray(r) ? r : r?.employees || r?.items || []
  );
}

export function createVendorEmployee(
  vendorId: string,
  data: Partial<VendorEmployee>
): Promise<VendorEmployee> {
  return req(`/vendors/${vendorId}/employees`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Trigger the tenant-flow activation cascade for this employee.
 * Backend mints activation_code, sends SMS + email w/ install link,
 * returns { activation_code, expires_at } (code omitted from response in prod).
 */
export function activateVendorEmployee(
  vendorId: string,
  employeeId: string,
  data?: { property_id?: string; channel?: 'sms' | 'email' | 'both' }
): Promise<{ activation_code?: string; expires_at?: string; sent_to?: string[] }> {
  return req(`/vendors/${vendorId}/employees/${employeeId}/activate?return_code=1`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
}

// ─── Grants ───────────────────────────────────────────────────────────────

export function listVendorGrants(vendorId: string): Promise<VendorGrant[]> {
  return req(`/vendors/${vendorId}/grants`).then((r) =>
    Array.isArray(r) ? r : r?.grants || r?.items || []
  );
}

export function createVendorGrant(
  vendorId: string,
  data: Partial<VendorGrant>
): Promise<VendorGrant> {
  return req(`/vendors/${vendorId}/grants`, {
    method: 'POST',
    body: JSON.stringify(data),
  }).then((r) => r?.grant || r);
}

export function revokeVendorGrant(
  vendorId: string,
  grantId: string
): Promise<VendorGrant> {
  return req(`/vendors/${vendorId}/grants/${grantId}/revoke`, {
    method: 'POST',
    body: JSON.stringify({}),
  });
}
